import React, { FC, Fragment } from 'react';
import { AppBar, Box, Divider, Hidden, List, styled, useEnovaContext } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';

import {
  AppRoute,
  camelCasify,
  ExpertToolsXMLPortfolioWorklistPath,
  getExpertToolsXMLPortfolioBasePath,
  getMainScreenPath,
  hasPermission,
  MainScreenTab
} from '../../../utils/navigation';
import { useUser } from '../../../hooks/useUser';
import RouterLink from '../../routerLink';
import { ActionBarHeight } from '../../../utils/layout';

import UserMenuCollapsed from './userMenuCollapsed';

const MenuContent = styled.div`
  .user-select--button {
    border: thin solid ${({ theme }) => theme.palette.divider};
    height: auto;
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const StyledMenuContent = styled(MenuContent)`
  margin-top: ${ActionBarHeight}px;
`;

const buildingsPath = getMainScreenPath({
  tab: MainScreenTab.BUILDINGS
});

const otherBuildingsPath = getMainScreenPath({
  tab: MainScreenTab.OTHERS_BUILDINGS
});

const findBuildingPath = getMainScreenPath({
  tab: MainScreenTab.SEARCH
});

const xmlRegistrationPath = getMainScreenPath({
  tab: MainScreenTab.EXPERT_REGISTRATION
});

const MainMenu: FC = () => {
  const { t } = useTranslation();
  const { hasUserProfile, user } = useUser();
  const { setMainMenuOpen, mainMenuOpen } = useEnovaContext();

  const buildingsMatch = useMatch(buildingsPath);
  const rootMatch = useMatch({
    path: '/'
  });

  const otherBuildingsMatch = useMatch(otherBuildingsPath);
  const findBuildingMatch = useMatch(findBuildingPath);
  const xmlRegistrationMatch = useMatch(xmlRegistrationPath);
  const statisticsMatch = useMatch(AppRoute.STATISTICS);
  const settingsMatch = useMatch(AppRoute.PROFILE);
  const xmlPortfolioMatch = useMatch(ExpertToolsXMLPortfolioWorklistPath);

  return (
    <AppBar.Menu
      ToggleButtonProps={{
        label: t('mainMenu.label'),
        'aria-label': mainMenuOpen ? t('mainMenu.close') : t('mainMenu.open')
      }}
    >
      <StyledMenuContent className="d-flex f-column f-fill">
        <Hidden mdUp>
          <Box padding={2}>
            <UserMenuCollapsed />
          </Box>

          <Divider />
        </Hidden>

        <nav>
          <List>
            {hasUserProfile && user && (
              <Fragment>
                <List.Item
                  {...{ to: buildingsPath }}
                  button
                  className="fvibs"
                  component={RouterLink}
                  divider
                  onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                  selected={!!buildingsMatch || !!rootMatch}
                >
                  {t(`mainScreen.tab.${camelCasify(MainScreenTab.BUILDINGS)}`, {
                    context: user.type
                  })}
                </List.Item>

                <List.Item
                  {...{ to: otherBuildingsPath }}
                  button
                  className="fvibs"
                  component={RouterLink}
                  divider
                  onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                  selected={!!otherBuildingsMatch}
                >
                  {t(`mainScreen.tab.${camelCasify(MainScreenTab.OTHERS_BUILDINGS)}`, { context: user.type })}
                </List.Item>

                <List.Item
                  {...{ to: findBuildingPath }}
                  button
                  className="fvibs"
                  component={RouterLink}
                  divider
                  onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                  selected={!!findBuildingMatch}
                >
                  {t(`mainScreen.tab.${camelCasify(MainScreenTab.SEARCH)}`, {
                    context: user.type
                  })}
                </List.Item>
                {hasPermission(AppRoute.EXPERT_TOOLS_XML_PORTFOLIO, user) && (
                  <List.Item
                    {...{ to: xmlRegistrationPath }}
                    button
                    className="fvibs"
                    component={RouterLink}
                    divider
                    onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                    selected={!!xmlRegistrationMatch}
                  >
                    {t(`mainScreen.tab.${camelCasify(MainScreenTab.EXPERT_REGISTRATION)}`, {
                      context: user.type
                    })}
                  </List.Item>
                )}

                <Box bgcolor="divider" height={20} width={1} />

                <List.Item
                  {...{ to: AppRoute.PROFILE }}
                  button
                  className="fvibs"
                  component={RouterLink}
                  onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                  selected={!!settingsMatch}
                >
                  {t('yourSettings')}
                </List.Item>

                <Box bgcolor="divider" height={20} width={1} />
              </Fragment>
            )}

            <List.Item
              {...{ to: AppRoute.STATISTICS }}
              button
              className="fvibs"
              component={RouterLink}
              divider
              onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
              selected={!!statisticsMatch}
            >
              {t('statistics')}
            </List.Item>

            {hasPermission(AppRoute.EXPERT_TOOLS_XML_PORTFOLIO, user) && (
              <List.Item
                {...{ to: getExpertToolsXMLPortfolioBasePath() }}
                button
                className="fvibs"
                component={RouterLink}
                divider
                onClick={(e) => !e.ctrlKey && setMainMenuOpen(false)}
                selected={!!xmlPortfolioMatch}
              >
                {t('mainMenu.item', { context: 'expertToolsPortfolio' })}
              </List.Item>
            )}
          </List>
        </nav>
      </StyledMenuContent>
    </AppBar.Menu>
  );
};

export default MainMenu;
